import { useMutation } from '@tanstack/react-query';
import { useDb } from './db';
import type { ViewContext } from './contexts';

/*
 * Bug reports (29.10, his «кнопка баг-репорта везде»): the team tests the site and the app and tells
 * the makers what is wrong — the words, screenshots, and what the app knows by itself (the page, the
 * role, the platform, the version, the device). A report goes to `bug_reports`, its screenshots to the
 * private bucket `bug-reports` under <user id>/<report id>/; the makers read them with
 * `node tools/dev/bugs.mjs`. Needs a connection.
 */

export interface BugScreenshot {
  /** a Blob in the browser; the bytes of a file on the phone */
  file: Blob | ArrayBuffer;
  extension: string;
  contentType: string;
}

export interface BugReport {
  ctx: ViewContext;
  platform: 'web' | 'ios' | 'android';
  appVersion?: string;
  page: string;
  device?: string;
  description: string;
  screenshots: ReadonlyArray<BugScreenshot>;
}

/** A random id (the phone has no `crypto.randomUUID`). */
const uuid = () =>
  globalThis.crypto?.randomUUID?.() ??
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

export function useReportBug() {
  const db = useDb();
  return useMutation({
    mutationFn: async (r: BugReport) => {
      const { data: auth } = await db.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) throw new Error('not signed in');
      const id = uuid();
      const paths: string[] = [];
      for (const [i, shot] of r.screenshots.entries()) {
        const path = `${userId}/${id}/${i + 1}.${shot.extension}`;
        const { error } = await db.storage
          .from('bug-reports')
          .upload(path, shot.file, { contentType: shot.contentType, upsert: false });
        if (error) throw error;
        paths.push(path);
      }
      const { error } = await db.from('bug_reports').insert({
        id,
        school_id: r.ctx.schoolId,
        member_id: r.ctx.memberId,
        role: r.ctx.kind,
        platform: r.platform,
        app_version: r.appVersion?.slice(0, 40) ?? null,
        page: r.page.slice(0, 300) || '/',
        device: r.device?.slice(0, 400) ?? null,
        description: r.description.trim().slice(0, 4000),
        screenshots: paths,
      });
      if (error) throw error;
      return id;
    },
  });
}
